import { BuildMode } from "@/types";
import { getEnumKeyByValue } from "@/utilities";
import * as Sentry from "@sentry/vue";
import { App } from "vue";

const mode = getEnumKeyByValue(import.meta.env.MODE, BuildMode);
const useSentryModes = [BuildMode.production, BuildMode.beta];

export const setupSentry = (app: App): void => {
  if (!useSentryModes.includes(mode)) {
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const release = __APP_VERSION__;

  Sentry.init({
    app,
    dsn,
    integrations: [
      Sentry.replayIntegration(),
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: true,
        history: true,
        xhr: true,
      }),
      Sentry.captureConsoleIntegration(),
    ],
    release,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      /^%c[0-9]{8}[a-z]? padding: 4px 8px; color: #fff; background: #32C380; border-radius: 5px$/, // Version log
    ],
    beforeSend(event) {
      // If you need more filters, store return of handleGoogleTagManagerError into variable and pass it into another handler. Return it at the end.
      return handleGoogleTagManagerError(event);
    },
  });
};

const handleGoogleTagManagerError = (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
  const filename = event.exception?.values?.[0]?.stacktrace?.frames?.slice(-1)[0]?.filename;
  if (filename && filename.includes("gtm.js")) {
    return null;
  } else {
    return event;
  }
};

export enum Event {
  ShareActionFallbacked = "Share action fallback",
}

export const trackEvent = async (event: Event, severity: Sentry.SeverityLevel = "info"): Promise<void> => {
  if (!useSentryModes.includes(mode)) {
    return;
  }

  Sentry.captureMessage(event, severity);
};
